import { createContext, useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import { useSnackbar } from 'notistack';

import useAuth from 'src/hooks/useAuth';
import usePrevious from 'src/hooks/usePrevious';
import { useServerRecognition } from 'src/hooks/useServerRecognition';
import useSettings, { SettingsKeys } from 'src/hooks/useSettings';
import { clientApi } from 'src/api/clientApi';
import logger from 'src/lib/logger';

const ConsentsContext = createContext({});
export const useConsents = () => useContext(ConsentsContext);

export const ConsentsContextProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { user, isInited, isAuthenticated, updateUser } = useAuth();
  const prevIsAuthenticated = usePrevious(isAuthenticated);
  const ssr = useServerRecognition();
  const settings = useSettings();
  const unauthorizedCookieTypesConsent =
    settings.state[SettingsKeys.unauthorizedCookieTypesConsent];

  const [missingMainConsents, setMissingMainConsents] = useState();
  const [missingCookiesConsent, setMissingCookiesConsent] = useState();

  useEffect(() => {
    const missingMainConsentsValue =
      user &&
      (!user.accepted_tos_at ||
        !user.accepted_privacy_policy_at ||
        user.marketing_consent === undefined);
    if (user && !_.isEqual(missingMainConsents, missingMainConsentsValue)) {
      setMissingMainConsents(missingMainConsentsValue);
    }

    const missingCookiesConsentValue =
      isInited &&
      _.isEmpty(user?.cookies_consent_types) &&
      _.isEmpty(unauthorizedCookieTypesConsent);
    if (!_.isEqual(missingCookiesConsent, missingCookiesConsentValue)) {
      setMissingCookiesConsent(missingCookiesConsentValue);
    }
  }, [
    user,
    ssr.error,
    missingMainConsents,
    missingCookiesConsent,
    unauthorizedCookieTypesConsent,
  ]);

  useEffect(() => {
    const f = async () => {
      const isLoggedIn = isAuthenticated && !prevIsAuthenticated;
      if (isLoggedIn && !_.isEmpty(unauthorizedCookieTypesConsent)) {
        await acceptCookiesPolicies(unauthorizedCookieTypesConsent);
        settings.actions.set(SettingsKeys.unauthorizedCookieTypesConsent, []);
      }
    };
    f();
  }, [isAuthenticated, prevIsAuthenticated, unauthorizedCookieTypesConsent]);

  const acceptCookiesPolicies = async (consent_types) => {
    try {
      if (isAuthenticated) {
        const updates = await clientApi.updateConsents({
          cookies_consent_types: consent_types,
        });
        updateUser(updates);
      } else {
        settings.actions.set(
          SettingsKeys.unauthorizedCookieTypesConsent,
          consent_types,
        );
      }
    } catch (e) {
      console.error(e);
      enqueueSnackbar(`Error: ${e.message}`, { variant: 'error' });
      logger.error(`acceptCookiesPolicies error: ${e.message}`);
    }
  };

  return (
    <ConsentsContext.Provider
      value={{
        missingMainConsents,
        missingCookiesConsent,
        acceptCookiesPolicies,
      }}
    >
      {children}
    </ConsentsContext.Provider>
  );
};
